import DOMPurify, { Config } from "dompurify";

const ALLOWED_TAGS = [
  "p",
  "br",
  "hr",
  "span",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "del",
  "ins",
  "mark",
  "small",
  "sub",
  "sup",
  "code",
  "pre",
  "blockquote",
  "a",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "ol",
  "ul",
  "li",
  "dl",
  "dt",
  "dd",
  "img",
  "figure",
  "figcaption",
  "div",
  "section",
  "article",
  "header",
  "footer",
  "aside",
  "table",
  "caption",
  "colgroup",
  "col",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "th",
  "td",
];

const allowedHtmlAttributes = [
  "href",
  "target",
  "rel",
  "src",
  "alt",
  "title",
  "style",
  "width",
  "height",
  "colspan",
  "rowspan",
  "scope",
  "start",
  "type",
];

const ALLOWED_URI_REGEXP =
  /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

const ALLOWED_STYLE_PROPERTIES = [
  "color",
  "background-color",
  "text-align",
  "text-indent",
  "text-transform",
  "line-height",
  "letter-spacing",
  "word-spacing",
  "vertical-align",
  "white-space",
  "word-break",
  "overflow-wrap",
  "width",
  "min-width",
  "max-width",
  "height",
  "min-height",
  "max-height",
  "display",
  "gap",
  "row-gap",
  "column-gap",
  "justify-content",
  "align-items",
  "align-content",
  "align-self",
  "caption-side",
  "table-layout",
];
const ALLOWED_STYLE_PREFIXES = [
  "font-",
  "text-decoration",
  "margin-",
  "padding-",
  "border-",
  "list-style-",
  "flex-",
];
const isAllowedStyleProperty = (property: string): boolean =>
  ALLOWED_STYLE_PROPERTIES.includes(property) ||
  ALLOWED_STYLE_PREFIXES.some((prefix) => property.startsWith(prefix));

const DOCUMENT_WRAPPERS = ["html", "head", "body"];

const SANITIZE_CONFIG: Config = {
  ALLOWED_TAGS,
  ALLOWED_ATTR: allowedHtmlAttributes,
  ALLOWED_URI_REGEXP,
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
};

const htmlPurifier = DOMPurify(window);
let removedStyles: string[] = [];

htmlPurifier.addHook("afterSanitizeAttributes", (node) => {
  if (!(node instanceof HTMLElement)) return;
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }

  if (!node.hasAttribute("style")) return;
  const { style } = node;
  for (let index = style.length - 1; index >= 0; index--) {
    const property = style.item(index);
    const value = style.getPropertyValue(property).toLowerCase();
    if (!isAllowedStyleProperty(property) || value.includes("url(")) {
      style.removeProperty(property);
      removedStyles.push(`style: ${property}`);
    }
  }
  if (!style.length) node.removeAttribute("style");
});

export interface ISanitizedRichHtml {
  html: string;
  removed: string[];
}

export const sanitizeRichHtml = (
  html: string | null | undefined,
): ISanitizedRichHtml => {
  const trimmed = html?.trim();
  if (!trimmed) return { html: "", removed: [] };

  removedStyles = [];
  const sanitized = htmlPurifier.sanitize(trimmed, SANITIZE_CONFIG);

  const removed = htmlPurifier.removed.flatMap((item) => {
    if ("element" in item) {
      const name = item.element.nodeName.toLowerCase();
      return name.startsWith("#") || DOCUMENT_WRAPPERS.includes(name)
        ? []
        : [`<${name}>`];
    }
    return item.attribute ? [item.attribute.name] : [];
  });

  return {
    html: sanitized,
    removed: Array.from(new Set([...removed, ...removedStyles])),
  };
};
